import React from "react";
import { Link } from "react-router-dom";
import PortalLayout from "@/components/ventrix/PortalLayout";
import { useApiQuery } from "@/hooks/useApi";
import { useAuth } from "@/context/AuthContext";
import { Kpi, formatMoney } from "@/components/ventrix/atoms";
import {
  Package, Clock, AlertOctagon, CheckCircle2, DollarSign, TrendingUp, Users as UsersIcon,
  ClipboardList, BarChart3, LayoutGrid, CreditCard, Bell, MapPin, ArrowRight,
} from "lucide-react";

function AdminCard({ to, Icon, title, desc }) {
  return (
    <Link to={to} className="group vtx-card vtx-card-interactive p-5" data-testid={`admin-card-${title.toLowerCase().split(" ")[0]}`}>
      <div className="flex items-start justify-between">
        <div className="w-10 h-10 grid place-items-center bg-[#F4FEFF] border border-[#A9C0E0]/60">
          <Icon size={20} color="#0E2F76" strokeWidth={2.2} />
        </div>
        <ArrowRight size={14} className="text-[#d41f26] group-hover:translate-x-1 transition-transform" />
      </div>
      <div className="mt-4 font-bold text-[#0E2F76]">{title}</div>
      <div className="text-sm text-[#475569] mt-1 leading-relaxed">{desc}</div>
    </Link>
  );
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const superAdmin = user?.role === "super_admin";
  const { data: summary } = useApiQuery("admin-summary", "/v1/reports/summary");
  const { data: stats } = useApiQuery("admin-stats", "/v1/shipments/stats");
  const { data: users } = useApiQuery("admin-users-count", "/v1/admin/users?limit=1");

  const s = summary || {};
  const userCount = users?.total ?? "—";

  return (
    <PortalLayout section="admin">
      <div className="vtx-eyebrow">/ Admin · Overview</div>
      <h1 className="mt-2 text-3xl font-black text-[#0E2F76] tracking-tighter">Admin dashboard</h1>
      <p className="text-sm text-[#475569] mt-1">Company-wide health, revenue and administration in one place.</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        <Kpi label="Total shipments" value={s.total_shipments ?? "—"} Icon={Package} />
        <Kpi label="Awaiting approval" value={stats?.awaiting_approval ?? "—"} Icon={Clock} accent="#B45309" />
        <Kpi label="Exceptions" value={s.exceptions ?? stats?.exceptions ?? "—"} Icon={AlertOctagon} accent="#d41f26" />
        <Kpi label="Delivered" value={s.delivered ?? stats?.delivered ?? "—"} Icon={CheckCircle2} accent="#166534" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
        <Kpi label="Revenue paid" value={s.revenue_paid != null ? formatMoney(s.revenue_paid) : "—"} Icon={DollarSign} accent="#166534" />
        <Kpi label="Outstanding" value={s.revenue_outstanding != null ? formatMoney(s.revenue_outstanding) : "—"} Icon={DollarSign} accent="#d41f26" />
        <Kpi label="On-time %" value={s.on_time_pct != null ? `${s.on_time_pct}%` : "—"} Icon={TrendingUp} accent="#1D4ED8" />
        <Kpi label="Users" value={userCount} Icon={UsersIcon} />
      </div>

      <div className="mt-8 vtx-eyebrow">Manage</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-3">
        <AdminCard to="/admin/users" Icon={UsersIcon} title="Users & roles" desc="Invite, disable and set roles for staff and customers." />
        <AdminCard to="/admin/audit" Icon={ClipboardList} title="Audit log" desc="Every change with actor, IP and timestamp." />
        <AdminCard to="/admin/reports" Icon={BarChart3} title="Reports" desc="Volumes, on-time performance and routing P&L." />
        <AdminCard to="/admin/services" Icon={LayoutGrid} title="Services" desc="Manage the services shown on the public site." />
        {superAdmin && <AdminCard to="/admin/settings/payments" Icon={CreditCard} title="Payments" desc="Enable methods and manage gateway keys." />}
        {superAdmin && <AdminCard to="/admin/settings/notifications" Icon={Bell} title="Notifications" desc="Email and SMS providers for shipment updates." />}
        {superAdmin && <AdminCard to="/admin/settings/maps" Icon={MapPin} title="Maps" desc="Tile provider and live vessel tracking." />}
      </div>
    </PortalLayout>
  );
}
